import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { TrackingPageDiscountSource } from "@redotech/redo-discounts-common/generated-discount-code-definition";
import { useTranslation } from "@redotech/redo-i18n";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import { memo, useState } from "react";
import { toast } from "../../../alert";
import { copyToClipboard } from "../../../clipboard";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import { TrackingPageContext } from "../../context/tracking-page-context";
import * as styles from "./discount-code-display.module.css";

export const DiscountCodeDisplay = memo(function DiscountCodeDisplay({
  discountId,
}: {
  discountId: string;
}) {
  const trackingPageContext = useRequiredContext(TrackingPageContext);
  const { t } = useTranslation();
  const orderId = trackingPageContext.order?.internalMongoId;

  const cacheKey = `game-discount-${orderId}-${discountId}`;

  const [cachedCode, setCachedCode] = useState<string | null>(() => {
    return localStorage.getItem(cacheKey);
  });

  const discountCodeResult = useLoad(async () => {
    if (cachedCode) {
      return cachedCode;
    }

    const newCode = await trackingPageContext.generateDiscountCode(
      discountId,
      TrackingPageDiscountSource.GAME,
      orderId,
    );

    if (!newCode.discountCode) {
      return undefined;
    }

    localStorage.setItem(cacheKey, newCode.discountCode);
    setCachedCode(newCode.discountCode);

    return newCode.discountCode;
  }, [trackingPageContext, discountId, orderId, cachedCode]);

  const copyWithNotification = async (text: string, label: string) => {
    await copyToClipboard(text);
    toast(t("{{label}} copied to clipboard", { label }), {
      variant: "success",
      duration: Temporal.Duration.from({ seconds: 1 }),
    });
  };

  const handleCopyCode = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (discountCodeResult.value) {
      try {
        await copyWithNotification(
          discountCodeResult.value,
          t("Discount code"),
        );
      } catch (error) {
        console.error("Failed to copy code:", error);
      }
    }
  };

  const renderContent = () => {
    if (discountCodeResult.pending) {
      return (
        <div className={styles.loadingCard}>
          <Flex align="center" justify="center" p="lg">
            <Text className={styles.messageText}>{t("Loading...")}</Text>
          </Flex>
        </div>
      );
    }

    if (discountCodeResult.error) {
      return (
        <div className={styles.errorCard}>
          <Flex align="center" justify="center" p="lg">
            <Text fontSize="md" textColor="error">
              {t("Error generating code")}
            </Text>
          </Flex>
        </div>
      );
    }

    return (
      <div className={styles.discountCard} onClick={(e) => handleCopyCode(e)}>
        <Flex align="center" justify="center" p="lg">
          <div className={styles.discountContent}>
            <Text className={styles.discountCode}>
              {discountCodeResult.value}
            </Text>
            <div className={styles.copyIcon}>
              <CopyIcon />
            </div>
          </div>
        </Flex>
      </div>
    );
  };

  return (
    <Flex dir="column" gap="md">
      {renderContent()}
    </Flex>
  );
});
